import React, { useState } from 'react';
import { WrapText, Copy, Trash2 } from 'lucide-react';

const TextWrapperTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [wrapLength, setWrapLength] = useState(80);
  const [wrapMode, setWrapMode] = useState('char'); // char, word
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [copied, setCopied] = useState(false);

  const wrapText = () => {
    if (!input.trim()) {
      setOutput('');
      return;
    }

    let result = '';
    const lines = input.split('\n');

    lines.forEach((line, lineIndex) => {
      if (wrapMode === 'char') {
        // Wrap at character limit
        let remaining = line;
        while (remaining.length > wrapLength) {
          result += prefix + remaining.substring(0, wrapLength) + suffix + '\n';
          remaining = remaining.substring(wrapLength);
        }
        if (remaining) {
          result += prefix + remaining + suffix;
        }
      } else {
        // Wrap at word boundaries
        const words = line.split(' ');
        let currentLine = '';
        
        words.forEach((word) => {
          if ((currentLine + ' ' + word).trim().length <= wrapLength) {
            currentLine = (currentLine + ' ' + word).trim();
          } else {
            if (currentLine) {
              result += prefix + currentLine + suffix + '\n';
            }
            currentLine = word;
          }
        });
        
        if (currentLine) {
          result += prefix + currentLine + suffix;
        }
      }
      
      if (lineIndex < lines.length - 1) {
        result += '\n';
      }
    });

    setOutput(result);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  const presets = [
    { name: 'Email (72)', length: 72 },
    { name: 'Terminal (80)', length: 80 },
    { name: 'Twitter (280)', length: 280 },
    { name: 'SMS (160)', length: 160 },
    { name: 'Code (120)', length: 120 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Wrap Length
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={wrapLength}
              onChange={(e) => setWrapLength(Math.max(1, parseInt(e.target.value) || 1))}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
              min="1"
            />
            <select
              value={wrapMode}
              onChange={(e) => setWrapMode(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
            >
              <option value="char">Characters</option>
              <option value="word">Words</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Presets
          </label>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset.name}
                onClick={() => setWrapLength(preset.length)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  wrapLength === preset.length
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Line Prefix (optional)
          </label>
          <input
            type="text"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="e.g., > or // or - "
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Line Suffix (optional)
          </label>
          <input
            type="text"
            value={suffix}
            onChange={(e) => setSuffix(e.target.value)}
            placeholder="e.g., \\ or |"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Input Text
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter text to wrap..."
          className="w-full h-40 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <button
        onClick={wrapText}
        disabled={!input.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <WrapText size={18} />
        Wrap Text
      </button>

      {output && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300">
              Wrapped Output
            </label>
            <span className="text-xs text-gray-500">
              {output.split('\n').length} lines
            </span>
          </div>
          <textarea
            value={output}
            readOnly
            className="w-full h-40 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm"
          />
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Result'}
        </button>
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear All
        </button>
      </div>
    </div>
  );
};

export default TextWrapperTool;
